/**
 * Helper function to get the present state based on the currentIndex in history
 * @param {State} state
 * @returns {WheelState}
 */
export const getPresentState = (state) =>
  state.segmentBuilder.history[state.segmentBuilder.currentIndex];

/**
 * Gets the type.
 * @param {State} state
 * @returns {number|null}
 */
export const getWheelType = (state) => getPresentState(state).type;

export const getHasUnsavedChanges = (state) =>
  state.segmentBuilder.hasUnsavedChanges;

// T22.109: how many teammate live co-edit ops have been applied (see
// `remoteOpSeq` in the segmentBuilder reducer).
export const getRemoteOpSeq = (state) => state.segmentBuilder.remoteOpSeq || 0;

// fe-builder-first-edit-after-load-silently-dropped: true when `addWheelData`
// just reconciled a real pre-hydration user edit on top of the server
// payload (see that reducer). The hydrate success handler reads this to skip
// its own unconditional markChangesSaved() for that one hydration, so the
// edit stays dirty and the normal autosave path actually persists it.
export const getPreHydrationEditPending = (state) =>
  state.segmentBuilder.preHydrationEditPending;

/**
 * True when there is an earlier state to undo back to.
 * @param {State} state
 * @returns {boolean}
 */
export const getCanUndo = (state) => state.segmentBuilder.currentIndex > 0;

/**
 * True when there is a later (redo) state to move forward to.
 * @param {State} state
 * @returns {boolean}
 */
export const getCanRedo = (state) =>
  state.segmentBuilder.currentIndex <
  state.segmentBuilder.history.length - 1;

/**
 * Monotonic counter bumped only by undo/redo. Consumers (e.g. SegmentView) watch it to
 * re-hydrate the craft.js editor canvas after an undo/redo without re-firing on live edits.
 * @param {State} state
 * @returns {number}
 */
export const getUndoRedoRevision = (state) =>
  state.segmentBuilder.undoRedoRevision || 0;

/**
 * Gets the business box.
 * @param {State} state
 * @returns {BusinessBox}
 */
export const getBusinessBox = (state) => getPresentState(state).business_box;

/**
 * Gets the is_primary flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getIsPrimary = (state) => getPresentState(state).is_primary;

/**
 * Gets the is_public flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getIsPublic = (state) => getPresentState(state).is_public;

/**
 * Gets the is_locked flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getIsLocked = (state) => getPresentState(state).is_locked;

/**
 * Gets the show_business_box flag.
 * @param {State} state
 * @returns {boolean|null}
 */
export const getShowBusinessBox = (state) =>
  getPresentState(state).show_business_box;

export const getWheelBusinessBox = (state) =>
  getPresentState(state).business_box;

/**
 * Gets the wheel name.
 * @param {State} state
 * @returns {string}
 */
export const getWheelName = (state) => getPresentState(state).wheel_name;

/**
 * Gets the description.
 * @param {State} state
 * @returns {string}
 */
export const getDescription = (state) => getPresentState(state).description;

/**
 * Gets the sub title.
 * @param {State} state
 * @returns {string}
 */
export const getSubTitle = (state) => getPresentState(state).sub_title;

/**
 * Gets the creator-picked Discover category slug (one of EXPLORE_CATEGORIES, or null).
 * @param {State} state
 * @returns {string|null}
 */
export const getCategory = (state) => getPresentState(state).category;
// ISP-9999 (gordon-0904-wheel-categories-multi5-plus-custom-tags)
export const getCategories = (state) => getPresentState(state).categories;
export const getWheelTags = (state) => getPresentState(state).wheel_tags;

/**
 * Gets the wheel segments.
 * @param {State} state
 * @returns {WheelSegment[]}
 */
export const getWheelSegments = (state) =>
  getPresentState(state).wheel_segments;
export const getWheelAutoResizing = (state) =>
  getPresentState(state).automatic_resizing;
export const getWheelContentHeights = (state) =>
  getPresentState(state).content_heights;
export const getWheelIsDraftValue = (state) => getPresentState(state).is_draft;
export const getWheelIsLiveValue = (state) => getPresentState(state).is_live;
export const getWheelIsClonableValue = (state) =>
  getPresentState(state).is_clonable;

// minors-13plus B.14: the creator's maturity self-label ("general" | "mature").
// Absent on wheels saved before the field shipped, so callers normalise through
// isMatureRating() rather than trusting a bare truthiness check.
export const getWheelContentRating = (state) =>
  getPresentState(state).content_rating;

// T22.153: only an explicit false disables (absent field = BE default True).
export const getWheelCommentsEnabled = (state) =>
  getPresentState(state).comments_enabled !== false;

export const getWheelId = (state) => getPresentState(state).id;

/**
 * Gets the background.
 * @param {State} state
 * @returns {Background}
 */
export const getBackground = (state) => getPresentState(state).background;

/**
 * Gets the image links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getImageLinks = (state) => getPresentState(state).image_links;

/**
 * Gets the video links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getVideoLinks = (state) => getPresentState(state).video_links;

/**
 * Gets the document links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getDocumentLinks = (state) =>
  getPresentState(state).document_links;

/**
 * Gets the audio links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getAudioLinks = (state) => getPresentState(state).audio_links;

/**
 * Gets the starred links.
 * @param {State} state
 * @returns {Link[]}
 */
export const getStarredLinks = (state) => getPresentState(state).starred_links;

/**
 * Gets the wheel image.
 * @param {State} state
 * @returns {string}
 */
export const getWheelImage = (state) =>
  getPresentState(state)?.wheel_center_media;

export const getCloningStatus = (state) => getPresentState(state).is_cloning;

export const getSegmentIcon = (state) => getPresentState(state).segment_icon;

export const getIsHidden = (state) => getPresentState(state).is_hidden;

export const getVisibility = (state) => getPresentState(state).visibility;

export const getIsWheelSavingLoader = (state) =>
  getPresentState(state).isSavingWheel;

export const getWheelTitle = (state) => getPresentState(state).wheel_name;

export const getWheelAuthorId = (state) => getPresentState(state).user;

// gordon-batch2-0925-teams-coedit-multiuser: server-authoritative, Teams-role-
// aware permission (workspaces.services.permissions.can(user, EDIT, wheel) —
// true for the wheel's owner AND for any Editor/Admin/Owner on the wheel's
// shared workspace). Undefined until the wheel has actually been fetched.
export const getIsEditable = (state) => getPresentState(state).is_editable;

// Top-level ephemeral UI field, outside undo/redo history — see
// initialHistoryState.selectedMenu in the reducer.
export const getSelectedMenu = (state) => state.segmentBuilder.selectedMenu;

export const getDesignTabTarget = (state) =>
  getPresentState(state).designTabTarget;

// W-A0: currently-active DesignTab sub-tab (top-level UI field, outside undo
// history). Non-null only while the Design pane is mounted; equals "Segments"
// only while the Resize subsection is open. Used to gate the wedge drag-resize
// overlay so its grips don't overlay every builder context.
export const getActiveDesignSubTab = (state) =>
  state.segmentBuilder.activeDesignSubTab || null;
// V1-RESIZE: whether the per-Segment Settings pane is showing the pure per-Segment
// resize view (top-level ephemeral UI field, outside undo history).
export const getSegmentResizeView = (state) =>
  !!state.segmentBuilder.segmentResizeView;
// W-A0: whether the user has explicitly entered "Resize Segment" mode via the
// side-pane toggle. The flag-gated wedge drag-resize overlay gates its mount on
// this, so the drag is inert unless the mode was deliberately turned on.
export const getResizeSegmentMode = (state) =>
  state.segmentBuilder.resizeSegmentMode || false;

export const getSidePaneVisible = (state) =>
  getPresentState(state).isSidePaneVisible;

export const getToolBarVisible = (state) =>
  getPresentState(state).isToolBarVisible;

export const getSegmentSetting = (state) =>
  getPresentState(state).isSegmentSettingVisible;

export const getRotateBoxVisibility = (state) =>
  getPresentState(state).is_rotate_box_open;

export const getSelectedSegment = (state) => {
  return getPresentState(state)?.wheel_segments?.find(
    (segment) => segment.selected === true
  );
};

export const getFroalaFocus = (state) => state.segmentBuilder.froala_focus;

export const getLockEmails = (state) =>
  getPresentState(state).lock_type.permission_lock.emails;
export const getLockAccessCodes = (state) =>
  getPresentState(state).lock_type.access_code_lock.access_codes;

export const getLockEmailPermission = (state) =>
  getPresentState(state).lock_type.permission_lock.is_active;

export const getLockAccessCodePermission = (state) =>
  getPresentState(state).lock_type.access_code_lock.is_active;

export const getGlobalSettings = (state) =>
  getPresentState(state).globalSettings;

export const getisGlobalSettingsApplied = (state) =>
  getPresentState(state).isGlobalSettingsApplied;

export const getisGlobalCornerRoundingApplied = (state) =>
  getPresentState(state).isGlobalCornerRoundingApplied;

export const getisGlobalBorderThicknessApplied = (state) =>
  getPresentState(state).isGlobalBorderThicknessApplied;

export const getisGlobalHeadingPropsApplied = (state) =>
  getPresentState(state).isGlobalHeadingPropsApplied;

export const getisGlobalParapgraphPropsApplied = (state) =>
  getPresentState(state).isGlobalParagraphPropsApplied;

export const getisGlobalLineHeightApplied = (state) =>
  getPresentState(state).isGlobalLineHeightApplied;

export const getisGlobalMarginApplied = (state) =>
  getPresentState(state).isGlobalMarginApplied;

export const getSegmentColorPalette = (state) => {
  const selectedArc = getPresentState(state)?.wheel_segments?.find(
    (segment) => segment.selected === true
  );

  const segment = getPresentState(state).wheel_segments.find(
    (seg) => seg.id === selectedArc?.id
  );
  return segment
    ? segment.settings.palette_data || segment.settings.paletteData
    : null;
};

export const getWheelColorPalette = (state) =>
  getPresentState(state).palette_data ||
  getPresentState(state).paletteData ||
  null;

export const getSelectedSegmentCharCount = (state) =>
  getPresentState(state).selected_segment_char_count || 0;
